/**
 * Debug Trace Logger
 *
 * Persistent debug logger that survives page reloads and hard navigations
 * (window.location.assign). Stores a ring buffer of entries in localStorage.
 *
 * Usage:
 *   import { trace } from "../utils/debugTrace";
 *   trace("Till", "guard fired", { tillSession: !!tillSession, tillAccess: typeof tillData?.tillAccess });
 *
 * To view the log:
 *   - DevTools Console: __dumpTrace()
 *   - Or: JSON.parse(localStorage.getItem("__debug_trace"))
 *
 * To clear:
 *   __clearTrace()
 *
 * Enable/disable:
 *   localStorage.setItem("__debug_enabled", "1")  — enable (DEFAULT ON for now)
 *   localStorage.setItem("__debug_enabled", "0")  — disable
 */

const STORAGE_KEY = "__debug_trace";
const MAX_ENTRIES = 200;

// Subscriber pattern — issueRecorder taps into this
const subscribers = new Set();

export function subscribe(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

function isEnabled() {
  const flag = localStorage.getItem("__debug_enabled");
  // Default ON — set to "0" to disable
  return flag !== "0";
}

function getEntries() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  } catch {
    return [];
  }
}

export function trace(source, message, data) {
  if (!isEnabled()) return;

  const entry = {
    t: new Date().toISOString().slice(11, 23), // HH:mm:ss.SSS
    src: source,
    msg: message,
  };
  if (data !== undefined) {
    entry.d = data;
  }

  // Notify subscribers (e.g. issue recorder)
  for (const fn of subscribers) {
    try { fn(entry); } catch {}
  }

  const entries = getEntries();
  entries.push(entry);
  // Ring buffer — keep last MAX_ENTRIES
  if (entries.length > MAX_ENTRIES) {
    entries.splice(0, entries.length - MAX_ENTRIES);
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // JSON.stringify can fail for reasons other than localStorage being full --
    // e.g. `data` contains a circular reference (a raw DOM/React event passed by
    // mistake). trace() must never throw into its caller, so each fallback step
    // is its own try/catch instead of assuming the retry will succeed.
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([entry]));
    } catch {
      try {
        const { d, ...entryWithoutData } = entry;
        localStorage.setItem(STORAGE_KEY, JSON.stringify([entryWithoutData]));
      } catch {
        // Give up silently -- logging must never break the feature that called it.
      }
    }
  }

  // Also log to console for DevTools (if open)
}

// Global helpers for DevTools console
if (typeof window !== "undefined") {
  window.__dumpTrace = () => {
    const entries = getEntries();
    console.table(entries);
    return entries;
  };
  window.__clearTrace = () => {
    localStorage.removeItem(STORAGE_KEY);
  };
}
